/**
 * PromptGenerator.js
 * 
 * Service for generating structured prompts for AI coding assistants.
 * Creates optimized prompts for each development phase.
 */

class PromptGenerator {
  constructor() {
    // System prompts for each phase (ultra-compact)
    this.SYSTEM_PROMPTS = {
      // Planning Phase (38 tokens)
      PLANNING: "You are a software project planner. Break down requirements into components and files. Estimate lines of code. Return ONLY JSON.",
      
      // Design Phase (45 tokens)
      DESIGN: "You are a software architect. Create detailed technical designs and algorithms. Organize files into batches. Return ONLY JSON.",
      
      // Coding Phase (39 tokens)
      CODING: "You are a programmer. Implement files based on specifications. Use proper error handling. Return ONLY JSON with complete code.",
      
      // Refinement Phase (44 tokens)
      REFINEMENT: "You are a code reviewer. Make precise updates to fix issues. Use line operations for surgical changes. Return ONLY JSON.",
      
      // Finishing Phase (42 tokens)
      FINISHING: "You are a code optimizer. Verify implementations and suggest improvements. Focus on performance and security. Return ONLY JSON."
    };
  }

  /**
   * Generate planning phase prompt
   * @param {string} requirements - Project requirements
   * @returns {string} Formatted prompt
   */
  async generatePlanningPrompt(requirements) {
    return `
${this.SYSTEM_PROMPTS.PLANNING}

Create a project plan for the following requirements:

REQUIREMENTS:
${requirements}

Break down the project into logical components, identify necessary files, and estimate development effort.

Focus on:
1. Identifying all necessary components
2. Mapping dependencies between components
3. Prioritizing implementation order
4. Estimating lines of code per file

RESPONSE FORMAT:
{"v":1,"p":"P","a":"GEN","i":"initial_plan","c":{"cp":[{"id":"","n":"","d":"","f":[{"p":"","e":0,"d":""}],"dep":[]}],"seq":[],"est":0}}

Return ONLY the JSON with no explanation text.
`;
  }

  /**
   * Generate design phase prompt
   * @param {Array} components - Components from planning phase
   * @param {string} projectId - Project ID
   * @returns {string} Formatted prompt
   */
  async generateDesignPrompt(components, projectId) {
    return `
${this.SYSTEM_PROMPTS.DESIGN}

Create technical design specifications for the following project components:

COMPONENTS:
${JSON.stringify(components, null, 2)}

For each file, provide:
1. Detailed technical specifications
2. Interface definitions (exports/imports)
3. Algorithm specifications where needed
4. Implementation complexity

Then organize files into implementation batches based on dependencies.

RESPONSE FORMAT:
{"v":1,"p":"D","a":"GEN","i":"tech_design","c":{"fs":[{"p":"","e":0,"d":"","exp":[],"imp":[],"alg":[]}],"bat":[{"id":"","f":[],"p":0}]}}

Return ONLY the JSON with no explanation text.
`;
  }

  /**
   * Generate coding phase prompt for implementing a batch
   * @param {Object} batch - Batch to implement
   * @param {string} projectId - Project ID
   * @returns {string} Formatted prompt
   */
  async generateCodingPrompt(batch, projectId) {
    // This would normally get files from storage using projectId
    // For now, implementing a simplified version
    const batchFileIds = batch.files || [];
    
    return `
${this.SYSTEM_PROMPTS.CODING}

Implement the following batch of files:

BATCH_ID: ${batch.id}
FILES: ${JSON.stringify(batchFileIds)}

For each file, provide complete and correct implementations following best practices:
- Include proper error handling
- Add appropriate comments
- Follow consistent code style
- Use modern language features

RESPONSE FORMAT:
{"v":1,"p":"C","a":"GEN","i":"${batch.id}","c":{"f":[{"p":"path/to/file.ext","l":"language","e":0,"a":0,"c":"content"}]}}

Return ONLY the JSON with no explanation text.
`;
  }

  /**
   * Generate continuation prompt for incomplete implementation
   * @param {string} lastCompleteLine - Last complete line
   * @param {string} filePath - File path being continued
   * @returns {string} Formatted prompt
   */
  async generateContinuationPrompt(lastCompleteLine, filePath) {
    return `
${this.SYSTEM_PROMPTS.CODING}

Continue the implementation from the last complete line:

FILE: ${filePath}
LAST COMPLETE LINE:
${lastCompleteLine}

Start with this exact line and continue implementing the file.
Ensure the response is properly formatted.

RESPONSE FORMAT:
{"v":1,"p":"C","a":"GEN","i":"continuation","c":{"f":[{"p":"${filePath}","l":"language","c":"full content from beginning to end"}]}}

Return ONLY the JSON with no explanation text.
`;
  }

  /**
   * Generate refinement phase prompt
   * @param {Object} file - File to refine
   * @param {string} updateType - Type of update (fix, performance, refactor, feature)
   * @param {string} description - Issue or enhancement description
   * @param {string} currentContent - Current file content
   * @returns {string} Formatted prompt
   */
  async generateRefinementPrompt(file, updateType, description, currentContent) {
    return `
${this.SYSTEM_PROMPTS.REFINEMENT}

Make ${updateType} updates to the following file:

FILE: ${file.path}
LANGUAGE: ${file.language}
ISSUE/ENHANCEMENT DESCRIPTION:
${description}

CURRENT CONTENT:
\`\`\`
${currentContent}
\`\`\`

Provide precise changes using line operations:
- R: Replace lines (start, end, new content)
- F: Replace function (name, new content)
- C: Replace class method (class name, method name, new content)
- I: Import operations (add, remove, replace)

RESPONSE FORMAT:
{"v":1,"p":"R","a":"UPD","i":"update_${file.id}","c":{"p":"${file.path}","o":[{"t":"R","s":0,"e":0,"c":"new content"}]}}

Return ONLY the JSON with no explanation text.
`;
  }

  /**
   * Generate finishing phase prompt
   * @param {Object} project - Project data
   * @param {Array} files - Project files
   * @returns {string} Formatted prompt
   */
  async generateFinishingPrompt(project, files) {
    // Create file summary for the prompt
    const fileSummaries = files.map(file => ({
      path: file.path,
      language: file.language,
      loc: file.actualLoc || file.estimatedLoc,
      exports: file.exports || []
    }));

    return `
${this.SYSTEM_PROMPTS.FINISHING}

Review and optimize the following project:

PROJECT: ${project.name}
DESCRIPTION: ${project.description || 'No description'}

FILES:
${JSON.stringify(fileSummaries, null, 2)}

Analyze the project structure and suggest:
1. Performance optimizations
2. Security improvements
3. Code quality enhancements
4. Potential issues to address

RESPONSE FORMAT:
{"v":1,"p":"F","a":"GEN","i":"optimization","c":{"op":[{"p":"file_path","t":"optimization_type","d":"description","s":0,"e":0,"c":"suggested change"}]}}

Return ONLY the JSON with no explanation text.
`;
  }

  /**
   * Generate testing prompt for a specific file
   * @param {Object} file - File to test
   * @param {string} fileContent - Current file content
   * @returns {string} Formatted prompt
   */
  async generateTestingPrompt(file, fileContent) {
    return `
You are a software testing expert. Write comprehensive tests for the provided code. Focus on edge cases and robustness. Return ONLY JSON.

FILE: ${file.path}
LANGUAGE: ${file.language}

FILE CONTENT:
\`\`\`
${fileContent}
\`\`\`

Create thorough tests covering:
1. Main functionality
2. Edge cases
3. Error handling
4. Parameter validation

RESPONSE FORMAT:
{"v":1,"p":"T","a":"GEN","i":"tests_${file.id}","c":{"p":"test/${file.path}","l":"${file.language}","c":"test content"}}

Return ONLY the JSON with no explanation text.
`;
  }

  /**
   * Generate documentation prompt for a specific file
   * @param {Object} file - File to document
   * @param {string} fileContent - Current file content
   * @returns {string} Formatted prompt
   */
  async generateDocumentationPrompt(file, fileContent) {
    return `
You are a technical documentation writer. Create clear documentation for the provided code. Focus on usage examples and APIs. Return ONLY JSON.

FILE: ${file.path}
LANGUAGE: ${file.language}

FILE CONTENT:
\`\`\`
${fileContent}
\`\`\`

Document:
1. Purpose and overview
2. API/Function documentation
3. Usage examples
4. Dependencies and requirements

RESPONSE FORMAT:
{"v":1,"p":"D","a":"GEN","i":"docs_${file.id}","c":{"p":"docs/${file.path.replace(/\.[^.]+$/, '.md')}","l":"markdown","c":"documentation content"}}

Return ONLY the JSON with no explanation text.
`;
  }
}

export default PromptGenerator;
